import { useCallback, useMemo, useState } from 'react';

export type LoadableState<T> = {
  data: T;
  error: Error | undefined;
  hasError: boolean;
  hasEverLoaded: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  reset: () => void;
  setData: (
    d: T | ((prevState: T) => T),
    options?: { shouldSetLoaded?: boolean }
  ) => void;
  setError: (e: Error, options?: { shouldSetLoaded?: boolean }) => void;
  setLoaded: () => void;
  setLoading: () => void;
};

type Options = { initializeAsLoaded?: boolean } & (
  | {
      debug?: never;
      key?: string;
    }
  | {
      debug: 'error' | 'info' | 'log' | 'trace' | 'warn' | undefined;
      key: string;
    }
);

export const defaultLoadableState = <T>(
  data: T,
  options?: Options
): LoadableState<T> => ({
  data,
  error: undefined,
  hasError: false,
  hasEverLoaded: options?.initializeAsLoaded ?? false,
  isLoaded: options?.initializeAsLoaded ?? false,
  isLoading: false,
  reset() {},
  setData() {},
  setError() {},
  setLoaded() {},
  setLoading() {}
});

const defaultOptions: Required<Options> = {
  debug: undefined,
  initializeAsLoaded: false,
  key: 'unknown'
};

/**
 * This hook makes provides a common interface to track data that is loaded from
 * an API. It comes pre-baked with loaded, loading, and error states.
 *
 * @example
 * ```
 * const {
 *   data, error, isLoaded, isLoading, setData, setError, setLoading
 * } = useLoadableState(false);
 *
 * useEffect(() => {
 *   setLoading();
 *
 *   fetch('https://example.com')
 *     .then(res => res.json())
 *     .then(res => setData(res.isCool))
 *     .catch(setError);
 * }, [setData, setError, setLoading]);
 *
 * return { data, error, isLoaded, isLoading };
 * ```
 */

const useLoadableState = <T>(
  defaultData: T,
  options: Options = {}
): LoadableState<T> => {
  const opts: Required<Options> = { ...defaultOptions, ...options };

  const [initialData] = useState<T>(defaultData);
  const [data, setDataState] = useState<T>(initialData);
  const [error, setErrorState] = useState<Error | undefined>();
  const [hasEverLoaded, setHasEverLoaded] = useState(opts.initializeAsLoaded);
  const [isLoaded, setLoadedState] = useState(opts.initializeAsLoaded);
  const [isLoading, setLoadingState] = useState(false);

  const reset = useCallback(
    function reset() {
      setDataState(initialData);
      setErrorState(undefined);
      setLoadedState(false);
      setLoadingState(false);
    },
    [initialData]
  );

  const setOnLoaded = useCallback(() => {
    setHasEverLoaded(true);
    setLoadedState(true);
    setLoadingState(false);
  }, []);

  const setData = useCallback(
    function setData(
      d: T | ((prevState: T) => T),
      { shouldSetLoaded = true } = {}
    ) {
      if (opts.debug) {
        console[opts.debug]('LoadableState', opts.key, 'setting data', d);
      }

      setDataState(d);
      setErrorState(undefined);

      if (shouldSetLoaded) {
        setOnLoaded();
      }
    },
    [opts.debug, opts.key, setOnLoaded]
  );

  const setError = useCallback(
    function setError(e: Error, { shouldSetLoaded = true } = {}) {
      if (opts.debug) {
        console[opts.debug]('LoadableState', opts.key, 'setting error', e);
      }

      setErrorState(e);

      if (shouldSetLoaded) {
        setOnLoaded();
      }
    },
    [opts.debug, opts.key, setOnLoaded]
  );

  const setLoaded = useCallback(
    function setLoaded() {
      if (opts.debug) {
        console[opts.debug]('LoadableState', opts.key, 'setting loaded');
      }

      setLoadedState(true);
      setHasEverLoaded(true);
    },
    [opts.debug, opts.key]
  );

  const setLoading = useCallback(
    function setLoading() {
      if (opts.debug) {
        console[opts.debug]('LoadableState', opts.key, 'setting loading');
      }

      setLoadedState(false);
      setLoadingState(true);
      setErrorState(undefined);
    },
    [opts.debug, opts.key]
  );

  return useMemo<LoadableState<T>>(
    () => ({
      data,
      error,
      hasError: Boolean(error),
      hasEverLoaded,
      isLoaded,
      isLoading,
      reset,
      setData,
      setError,
      setLoaded,
      setLoading
    }),
    [
      data,
      error,
      hasEverLoaded,
      isLoaded,
      isLoading,
      reset,
      setData,
      setError,
      setLoaded,
      setLoading
    ]
  );
};

export default useLoadableState;
